// src/components/TransactionTable.jsx
import React from "react";
import {
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  CreditCard,
  Edit2,
  Trash2,
  FileText,
} from "lucide-react";
import clsx from "clsx";


const TransactionTable = ({
  filteredTransactions = [],
  onDelete,
  onEdit,
  onView,
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  if (filteredTransactions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="h-10 w-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No transactions found
        </h3>
        <p className="text-gray-500 text-sm">
          {filteredTransactions.length === 0
            ? "Add your first transaction to get started"
            : "No transactions match your current filters."}
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {filteredTransactions.map((transaction) => (
        <div
          key={transaction._id || transaction.id}
          className="p-4 hover:bg-gray-50 transition-all group"
        >
          <div className="flex items-center justify-between">
            {/* Left: Icon + Details */}
            <div className="flex items-center gap-4 flex-1">
              <div
                className={clsx(
                  "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0",
                  transaction.type === "income"
                    ? "bg-emerald-100 text-emerald-600"
                    : "bg-rose-100 text-rose-600"
                )}
              >
                {transaction.type === "income" ? (
                  <ArrowUpRight size={20} />
                ) : (
                  <ArrowDownRight size={20} />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">
                    {transaction.category}
                  </h3>
                  <span
                    className={clsx(
                      "px-2.5 py-1 rounded-full text-xs font-medium",
                      transaction.type === "income"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-rose-100 text-rose-700"
                    )}
                  >
                    {transaction.type}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    {formatDate(transaction.date)}
                  </div>
                  <div className="flex items-center gap-1">
                    <CreditCard size={14} />
                    {transaction.paymentMode || "Cash"}
                  </div>
                </div>
                {transaction.notes && (
                  <p className="text-sm text-gray-600 mt-1 line-clamp-1">
                    {transaction.notes}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Amount + Action Icons */}
            <div className="flex items-center gap-5">
              <div className="text-right">
                <p
                  className={clsx(
                    "text-xl font-bold",
                    transaction.type === "income"
                      ? "text-emerald-600"
                      : "text-rose-600"
                  )}
                >
                  {transaction.type === "income" ? "+" : "-"}₹
                  {transaction.amount.toLocaleString("en-IN")}
                </p>
              </div>

              {/* Action Icons - Always Visible on Hover */}
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                

                <button
                  onClick={() => onEdit?.(transaction)}
                  className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 size={18} className="text-blue-600" />
                </button>

                <button
                  onClick={() => onDelete(transaction._id || transaction.id)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 size={18} className="text-red-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TransactionTable;
